import React, { useEffect, useState } from 'react';
import { Flex, Text } from '@chakra-ui/react';
import { Pagination, FilterSelect } from '@morressier/alembic';
import {
  ACTIVITY_TYPE_INFO,
  IRoleType,
  ROLE_WISE_ACTIVITY_LOGS
} from '@publishing-engine/domain/models';
import { useQuery } from '@tanstack/react-query';
import { fetchActivityLog } from 'src/api/queries';
import useCurrentContextId from 'src/hooks/useCurrentContextId';
import { FETCH_ACTIVITY_LOG_KEY } from 'src/constants';
import { useForm } from 'react-hook-form';
import { IActivityLog } from '@publishing-engine/domain/models';
import ActivityBase from './ActivityBase';
import Loading from '../../../pages/Status/Loading';
import { useKeycloak } from 'src/components/AuthHOC';

interface ActivityTabProps {
  roleContext: IRoleType;
  submissionContextId?: string;
  submissionId?: string;
  reviewId?: string;
  fieldLabelMap?: Record<string, string>;
}
export type ActivityLogResponse = IActivityLog & { emailCount: number };

const LIMIT = 20;

/**
 * A single checklist checkbox toggle is checklist state, not activity. Only
 * bulk checklist updates (more than one changed field) and the completion
 * confirmation stay visible, so a checklist produces one summary entry instead
 * of one entry per checkbox. The underlying events are left untouched; the
 * filtering happens when the activity log is read.
 */
const isVisibleActivity = (activity: IActivityLog) => {
  if (activity.type !== 'EditorialFormDataCreated') return true;

  const changeCount = activity.changes?.length ?? 0;

  return activity.isAdminChecklistDone ? changeCount <= 1 : changeCount > 1;
};
const ActivityTab: React.FC<ActivityTabProps> = ({
  roleContext,
  submissionContextId,
  submissionId,
  reviewId,
  fieldLabelMap
}) => {
  // state
  const [page, setPage] = useState<number>(1);
  const { currentUser } = useKeycloak();
  const userId = currentUser?.id;
  // form
  const { control, watch, resetField } = useForm({
    defaultValues: {
      activityTypes: []
    }
  });

  const ctxId = useCurrentContextId() ?? submissionContextId;

  const selectedActivityTypes = watch('activityTypes') || [];

  // Reset page to 1 when filter changes
  useEffect(() => {
    setPage(1);
  }, [selectedActivityTypes]);

  const fetchActivities = (page = 1) => {
    const offset = (page - 1) * LIMIT;
    return ctxId && submissionId
      ? fetchActivityLog<ActivityLogResponse>(
          submissionId,
          roleContext,
          userId,
          ctxId,
          offset,
          LIMIT,
          'descending',
          selectedActivityTypes.length === 1
            ? { eventType: selectedActivityTypes[0] }
            : { eventTypes: selectedActivityTypes }
        )
      : null;
  };

  const {
    data: activityLogData,
    isLoading,
    isError
  } = useQuery({
    queryKey: [
      FETCH_ACTIVITY_LOG_KEY,
      { submissionId, ctxId, page, selectedActivityTypes }
    ],
    queryFn: () => fetchActivities(page),
    keepPreviousData: true
  });

  const totalPages = Math.ceil((activityLogData?.total || 0) / LIMIT);
  const activityLogs = (activityLogData?.results || []).filter(
    isVisibleActivity
  );

  if (isLoading && !activityLogData) {
    return <Loading />;
  }

  if (isError) {
    return <Text>Error loading activity logs.</Text>;
  }

  // Create activity type options for the filter based upon user role
  const isEventAllowedForRole = (
    role: IRoleType,
    eventType: string
  ): boolean => {
    const roleConfig = ROLE_WISE_ACTIVITY_LOGS[role];

    if (!roleConfig) return false;

    if (roleConfig.allowedEventTypes === 'ALL') {
      return true;
    }

    return roleConfig.allowedEventTypes.includes(eventType);
  };

  const activityTypeOptions = Object.entries(ACTIVITY_TYPE_INFO)
    .filter(([, value]) => value.type !== 'SubmissionAccessed')
    .filter(([, value]) =>
      isEventAllowedForRole(roleContext as IRoleType, value.type)
    )
    .map(([, value]) => ({
      value: value.type,
      name: value.label
    }));

  return (
    <Flex flexDirection="column" gap={8}>
      <Flex flexDirection="column" alignItems="flex-start" gap={2}>
        <Text textStyle="headingSection">Activity</Text>
        <Text textStyle="textBody">
          View and monitor all actions and updates in this submission through
          activity logs.
        </Text>
      </Flex>

      <Flex justifyContent="space-between" alignItems="center">
        <FilterSelect
          name="activityTypes"
          control={control}
          itemLabel="Activity type"
          watch={watch}
          clearSelection={() => resetField('activityTypes')}
          items={activityTypeOptions}
          pluralLabel="activity types"
          width="450px"
        />
      </Flex>

      <Text textStyle="headingLine">{activityLogData?.total} Activities</Text>
      {activityLogs.map((activity, index) => (
        <ActivityBase
          key={`${activity.eventId}-${activity.date}-${index}`}
          roleContext={roleContext}
          reviewId={reviewId}
          fieldLabelMap={fieldLabelMap}
          {...activity}
        />
      ))}

      <Flex justifyContent="center">
        {activityLogs.length === 0 ? (
          <Flex flexDirection="column" gap={2} textAlign="center">
            <Text textStyle="headingTitle">No activities found.</Text>
            <Text textStyle="textBody">
              We could not find any results. Please adjust your selection, and
              try again.
            </Text>
          </Flex>
        ) : (
          <Pagination
            key={totalPages}
            ariaLabel="Activity log pagination"
            currentPage={page}
            totalPages={totalPages || 1}
            onPageChange={setPage}
          />
        )}
      </Flex>
    </Flex>
  );
};

export default ActivityTab;
